/* eslint-disable no-useless-escape */
import { create } from "zustand";
import $api from "@/zapi-frontend/shared/api/axios";
import { TComment } from "../types/types";
import { STUB_COMMENT_ID } from "../../const/const";

type CommentStorage = {
  [chapterId: string]: TComment[];
};

type LoadingState = {
  [chapterId: string]: boolean;
};

type ErrorState = {
  [chapterId: string]: string | null;
};

type TCommentStore = {
  commentsStorage: CommentStorage;
  loadingStates: LoadingState;
  commentsError: ErrorState;

  getComments: (chapterId: string) => TComment[];
  isLoadingComments: (chapterId: string) => boolean;
  getCommentsError: (chapterId: string) => string | null;
  fetchComments: (projectId: string, chapterId: string) => Promise<void>;
  invalidateCommentsCache: (chapterId: string) => void;
  createComment: (params: {
    projectId: string;
    chapterId: string;
    content: string;
    stubComment: TComment;
    parentId?: string;
  }) => Promise<{ id: string }>;
  updateComment: (params: {
    projectId: string;
    chapterId: string;
    commentId: string;
    content: string;
    parentId?: string;
  }) => Promise<void>;
  deleteComment: (projectId: string, chapterId: string, commentId: string) => Promise<void>;
};

export const useCommentStore = create<TCommentStore>((set, get) => ({
  commentsStorage: {},
  loadingStates: {},
  commentsError: {},

  getComments: (chapterId: string) => {
    return get().commentsStorage[chapterId] || [];
  },

  isLoadingComments: (chapterId: string) => {
    return get().loadingStates[chapterId] || false;
  },

  getCommentsError: (chapterId: string) => {
    return get().commentsError[chapterId] || null;
  },

  fetchComments: async (projectId, chapterId) => {
    const cachedComments = get().commentsStorage[chapterId];
    if (cachedComments && cachedComments.length > 0) {
      return;
    }

    if (get().loadingStates[chapterId]) {
      return;
    }

    try {
      set((state) => ({
        loadingStates: {
          ...state.loadingStates,
          [chapterId]: true
        },
        commentsError: {
          ...state.commentsError,
          [chapterId]: null
        }
      }));

      const api = $api;
      const response = await api.get<{ data: TComment[] }>(`/projects/${projectId}/chapters/${chapterId}/comments`);

      set((state) => ({
        commentsStorage: {
          ...state.commentsStorage,
          [chapterId]: response.data.data
        },
        loadingStates: {
          ...state.loadingStates,
          [chapterId]: false
        }
      }));
    } catch (error) {
      console.error("Failed to fetch comments:", error);
      set((state) => ({
        commentsError: {
          ...state.commentsError,
          [chapterId]: error instanceof Error ? error.message : "Failed to fetch comments"
        },
        loadingStates: {
          ...state.loadingStates,
          [chapterId]: false
        }
      }));
    }
  },

  invalidateCommentsCache: (chapterId: string) => {
    set((state) => {
      const newCommentsStorage = { ...state.commentsStorage };
      delete newCommentsStorage[chapterId];
      return {
        commentsStorage: newCommentsStorage,
        loadingStates: { ...state.loadingStates, [chapterId]: false },
        commentsError: { ...state.commentsError, [chapterId]: null }
      };
    });
  },

  createComment: async ({ projectId, chapterId, content, stubComment, parentId }) => {
    try {
      if (!parentId) {
        set((state) => ({
          commentsStorage: {
            ...state.commentsStorage,
            [chapterId]: [...(state.commentsStorage[chapterId] || []), stubComment]
          }
        }));
      } else {
        set((state) => {
          const list = state.commentsStorage[chapterId] || [];
          const nextList = list.map((c) => (c.id === parentId ? { ...c, replies: [...c.replies, stubComment] } : c));
          return {
            commentsStorage: {
              ...state.commentsStorage,
              [chapterId]: nextList
            }
          };
        });
      }

      const api = $api;
      const response = await api.post<{ data: TComment }>(`/projects/${projectId}/chapters/${chapterId}/comments`, {
        content,
        parentId
      });

      if (!parentId) {
        set((state) => ({
          commentsStorage: {
            ...state.commentsStorage,
            [chapterId]: [...(state.commentsStorage[chapterId] || []).slice(0, -1), response.data.data]
          }
        }));
      } else {
        set((state) => {
          const list = state.commentsStorage[chapterId] || [];
          const nextList = list.map((c) => {
            if (c.id !== parentId) return c;
            const replies = c.replies.length ? [...c.replies.slice(0, -1), response.data.data] : [response.data.data];
            return { ...c, replies };
          });
          return {
            commentsStorage: {
              ...state.commentsStorage,
              [chapterId]: nextList
            }
          };
        });
      }

      return { id: response.data.data.id };
    } catch (error) {
      console.error("Failed to create comment:", error);
      get().invalidateCommentsCache(chapterId);
      set((state) => ({
        commentsError: {
          ...state.commentsError,
          [chapterId]: error instanceof Error ? error.message : "Failed to create comment"
        }
      }));
      return { id: STUB_COMMENT_ID };
    }
  },

  updateComment: async ({ projectId, chapterId, commentId, content, parentId }) => {
    if (parentId) {
      set((state) => {
        const list = state.commentsStorage[chapterId] || [];
        const nextList = list.map((c) => {
          if (c.id !== parentId) return c;
          const replies = c.replies.map((reply) => (reply.id === commentId ? { ...reply, content } : reply));
          return { ...c, replies };
        });
        return {
          commentsStorage: {
            ...state.commentsStorage,
            [chapterId]: nextList
          }
        };
      });
    } else {
      set((state) => ({
        commentsStorage: {
          ...state.commentsStorage,
          [chapterId]: (state.commentsStorage[chapterId] || []).map((comment) =>
            comment.id === commentId ? { ...comment, content } : comment
          )
        }
      }));
    }

    try {
      const api = $api;
      const response = await api.patch<{ data: TComment }>(
        `/projects/${projectId}/chapters/${chapterId}/comments/${commentId}`,
        { content }
      );

      set((state) => ({
        commentsStorage: {
          ...state.commentsStorage,
          [chapterId]: (state.commentsStorage[chapterId] || []).map((comment) =>
            comment.id === commentId ? response.data.data : comment
          )
        }
      }));
    } catch (error) {
      console.error("Failed to update comment:", error);
      get().invalidateCommentsCache(chapterId);
      set((state) => ({
        commentsError: {
          ...state.commentsError,
          [chapterId]: error instanceof Error ? error.message : "Failed to update comment"
        }
      }));
    }
  },

  deleteComment: async (projectId, chapterId, commentId) => {
    set((state) => ({
      commentsStorage: {
        ...state.commentsStorage,
        [chapterId]: (state.commentsStorage[chapterId] || [])
          .filter((comment) => comment.id !== commentId)
          .map((c) => ({ ...c, replies: c.replies.filter((c) => c.id !== commentId) }))
      }
    }));

    try {
      const api = $api;
      await api.delete(`/projects/${projectId}/chapters/${chapterId}/comments/${commentId}`);
    } catch (error) {
      console.error("Failed to delete comment:", error);
      get().invalidateCommentsCache(chapterId);
      set((state) => ({
        commentsError: {
          ...state.commentsError,
          [chapterId]: error instanceof Error ? error.message : "Failed to delete comment"
        }
      }));
    }
  }
}));
